import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

type StatusTextTone = 'success' | 'warning' | 'danger' | 'info' | 'destructive'
type StatusTextSize = 'xs' | 'sm' | 'base'

interface StatusTextProps {
    children: ReactNode
    tone: StatusTextTone
    size?: StatusTextSize
    muted?: boolean
    /**
     * Render with a subtle tone-matched background plus rounded corners so the text
     * stands out as an inline highlight (e.g. diff ranges in a monospace block).
     * Combines with `tone` to pick the bg variant.
     */
    highlight?: boolean
    truncate?: boolean
    title?: string
    className?: string
    'data-testid'?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const toneClasses: Record<StatusTextTone, string> = {
    success: 'text-status-success',
    warning: 'text-status-warning',
    danger: 'text-status-danger',
    info: 'text-status-info',
    destructive: 'text-destructive',
}
const toneMutedClasses: Record<StatusTextTone, string> = {
    success: 'text-status-success-muted',
    warning: 'text-status-warning-muted',
    danger: 'text-status-danger-muted',
    info: 'text-status-info-muted',
    destructive: 'text-destructive',
}
const toneHighlightClasses: Record<StatusTextTone, string> = {
    success: 'bg-status-success-subtle text-status-success dark:bg-status-success-subtle/40 dark:text-status-success-muted rounded-sm px-px',
    warning: 'bg-status-warning-subtle text-status-warning dark:bg-status-warning-subtle/40 dark:text-status-warning-muted rounded-sm px-px',
    danger: 'bg-status-danger-subtle text-status-danger dark:bg-status-danger-subtle/40 dark:text-status-danger-muted rounded-sm px-px',
    info: 'bg-status-info-subtle text-status-info dark:bg-status-info-subtle/40 dark:text-status-info-muted rounded-sm px-px',
    destructive: 'bg-destructive/10 text-destructive rounded-sm px-px',
}
const sizeClasses: Record<StatusTextSize, string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
}
/* eslint-enable i18next/no-literal-string */

export function StatusText({
    children,
    tone,
    size,
    muted,
    highlight,
    truncate,
    title,
    className,
    'data-testid': testId,
}: StatusTextProps) {
    const colorClass = highlight
        ? toneHighlightClasses[tone]
        : muted
            ? toneMutedClasses[tone]
            : toneClasses[tone]
    return (
        <span
            className={cn(colorClass, size && sizeClasses[size], truncate && 'truncate', className)}
            title={title}
            data-testid={testId}
        >
            {children}
        </span>
    )
}
